import clsx from 'clsx';
import { AlertTriangle, ExternalLink, ListChecks, Users } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { addDays } from '@pz/core';
import { PlanBar } from '../components/charts';
import { FormaIssueLink, formaLink, HoursInput, LocationSelects, ProjectCombobox, SourceSwitch, TaskBadges, type SourceFilter } from '../components/entry';
import { CellInput, LEVEL_BG, RangeNav, useWeekRange } from '../components/planning-common';
import { EMPTY_FIELD_FILTER, fieldFilterInput, TaskFieldFilters, type TaskFieldFilter } from '../components/task-filters';
import { Button, Checkbox, EmptyState, ErrorBox, Field, Input, Loading, Modal, Notice, PageHeader, Pill, Select, Tabs, td, th, useToast } from '../components/ui';
import { d, h, kw, pct } from '../lib/format';
import { useM, useQ, useTabParam } from '../lib/hooks';
import { ScrollTable, Sentinel, useChunked } from '../components/virtual';
import { useMe } from '../lib/me';
import { rowCheck, useRangeSelection } from '../lib/selection';

/**
 * Planung: the one place where project leads (and management) plan a project.
 * "Aufgaben": planned hours per task, the basis for plan/actual per task.
 * "Personal": planned hours per person and week, the basis for Auslastung.
 */
export function PlanningPage() {
  const { t } = useTranslation();
  const { can, isLead } = useMe();
  const [params, setParams] = useSearchParams();
  const projects = useQ('projects.list', {});
  const candidates = useMemo(
    () => (projects.data?.projects ?? []).filter((p) => !p.archived && p.billingMode !== 'internal' && (can('projects.manage', 'workload.manage') || isLead(p.id))),
    [projects.data, can, isLead],
  );
  const projectId = params.get('project') && candidates.some((c) => c.id === params.get('project')) ? params.get('project')! : candidates[0]?.id ?? '';
  const [tab, setTab] = useTabParam(['tasks', 'staff'] as const);
  const set = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    next.set(k, v);
    setParams(next);
  };
  if (projects.isLoading) return <Loading />;
  if (!candidates.length) return <EmptyState>{t('planning.noProjects')}</EmptyState>;
  return (
    <div>
      <PageHeader
        title={t('planning.title')}
        subtitle={t('planning.subtitle')}
        actions={
          <ProjectCombobox
            className="w-80"
            projects={candidates.map((p) => ({ id: p.id, number: p.number, name: p.name, formaName: p.formaName }))}
            value={projectId}
            onChange={(id) => id && set('project', id)}
            allLabel={t('planning.chooseProject')}
            clearable={false}
          />
        }
      />
      <Tabs
        value={tab}
        onChange={setTab}
        tabs={[
          { id: 'tasks', label: t('planning.tabs.tasks'), icon: <ListChecks className="size-4" /> },
          { id: 'staff', label: t('planning.tabs.staff'), icon: <Users className="size-4" /> },
        ]}
      />
      {projectId && tab === 'tasks' && <TaskPlanTab projectId={projectId} />}
      {projectId && tab === 'staff' && <StaffPlanTab projectId={projectId} />}
    </div>
  );
}

/**
 * Planned hours per task, entered inline (Forma issues and platform tasks); the plan is kept only in Projektzeit.
 * Empty = no plan, 0 = not evaluated. Filters by location, Forma category/type and the custom fields taken over.
 */
function TaskPlanTab({ projectId }: { projectId: string }) {
  const { t } = useTranslation();
  const { me } = useMe();
  const toast = useToast();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(true);
  const [source, setSource] = useState<SourceFilter>('all');
  const [top, setTop] = useState('');
  const [sub, setSub] = useState('');
  const [noPlan, setNoPlan] = useState(false);
  const [overPlan, setOverPlan] = useState(false);
  // filters on the loaded tasks: status, due date, plan, bookings; order of the list
  const [status, setStatus] = useState('');
  const [due, setDue] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [bookedFilter, setBookedFilter] = useState('');
  const [sortBy, setSortBy] = useState('ref');
  const [fields, setFields] = useState<TaskFieldFilter>(EMPTY_FIELD_FILTER);
  useEffect(() => {
    setFields(EMPTY_FIELD_FILTER);
    setStatus('');
    setDue('');
    setPlanFilter('');
    setBookedFilter('');
    setSortBy('ref');
  }, [projectId]);
  const [bulk, setBulk] = useState(false);
  const [bulkMinutes, setBulkMinutes] = useState<number | null>(null);
  const project = useQ('projects.get', { id: projectId });
  const locations = useQ('tasks.locations', { projectId }, { enabled: !!project.data?.project.forma.projectId });
  const hasLocations = !!locations.data?.nodes.length;
  const list = useQ('tasks.list', {
    projectId,
    q: q || undefined,
    statusGroup: open ? 'active' : undefined,
    source: source === 'all' ? undefined : source,
    locationId: sub || top || undefined,
    overPlan: overPlan || undefined,
    ...(source === 'manual' ? {} : fieldFilterInput(fields)),
  });
  const setPlan = useM('tasks.setPlan', { invalidate: ['tasks.', 'kpi.', 'cockpit.', 'dashboard.'] });
  const setPlanMany = useM('tasks.setPlanMany', { invalidate: ['tasks.', 'kpi.', 'cockpit.', 'dashboard.'] });
  const statuses = useMemo(() => [...new Set((list.data?.tasks ?? []).map((x) => x.status))].sort(), [list.data]);
  const quickFilters = [status, due, planFilter, bookedFilter].filter(Boolean).length;
  const rows = useMemo(() => {
    const today = me.today;
    const in7 = addDays(today, 7);
    const in14 = addDays(today, 14);
    let r = (list.data?.tasks ?? []).filter((x) => !noPlan || x.plannedMinutes === null);
    if (status) r = r.filter((x) => x.status === status);
    if (due === 'overdue') r = r.filter((x) => x.statusGroup === 'active' && !!x.dueDate && x.dueDate < today);
    else if (due === 'd7') r = r.filter((x) => !!x.dueDate && x.dueDate >= today && x.dueDate <= in7);
    else if (due === 'd14') r = r.filter((x) => !!x.dueDate && x.dueDate >= today && x.dueDate <= in14);
    else if (due === 'none') r = r.filter((x) => !x.dueDate);
    if (planFilter === 'none') r = r.filter((x) => x.plannedMinutes === null);
    else if (planFilter === 'zero') r = r.filter((x) => x.plannedMinutes === 0);
    else if (planFilter === 'set') r = r.filter((x) => !!x.plannedMinutes);
    if (bookedFilter === 'none') r = r.filter((x) => !x.bookedMinutes);
    else if (bookedFilter === 'some') r = r.filter((x) => !!x.bookedMinutes);
    if (sortBy === 'due') r = [...r].sort((a, b) => (a.dueDate ?? '9999').localeCompare(b.dueDate ?? '9999'));
    else if (sortBy === 'booked') r = [...r].sort((a, b) => (b.bookedMinutes ?? 0) - (a.bookedMinutes ?? 0));
    else if (sortBy === 'plan') r = [...r].sort((a, b) => (b.plannedMinutes ?? 0) - (a.plannedMinutes ?? 0));
    return r;
  }, [list.data, noPlan, status, due, planFilter, bookedFilter, sortBy, me.today]);
  const chunk = useChunked(rows, 100, JSON.stringify([projectId, q, open, source, top, sub, noPlan, overPlan, fields, status, due, planFilter, bookedFilter, sortBy]));
  const order = useMemo(() => rows.map((x) => x.id), [rows]);
  const sel = useRangeSelection(order);
  useEffect(() => sel.clear(), [projectId]); // eslint-disable-line react-hooks/exhaustive-deps
  const canPlan = !!list.data?.canPlan;
  // trade, floor and the like under the title (the fields taken over from Forma)
  const fieldsShown = useMemo(() => new Set((project.data?.attributeDefs ?? []).filter((a) => a.mirrored && !a.formaDeleted).map((a) => a.id)), [project.data]);

  const save = (id: string, minutes: number | null) =>
    setPlan
      .mutateAsync({ id, plannedMinutes: minutes })
      .then(() => toast.ok(t('common.saved')))
      .catch(toast.error);

  const applyBulk = (minutes: number | null) =>
    setPlanMany
      .mutateAsync({ ids: [...sel.selected], plannedMinutes: minutes })
      .then((r) => {
        toast.ok(t('planning.bulkDone', { count: r.updated }));
        sel.clear();
        setBulk(false);
      })
      .catch(toast.error);

  return (
    <div className="flex flex-col gap-3 pb-16">
      <Notice tone="neutral">{t('planning.tasksInfo')}</Notice>
      <div className="flex flex-col gap-2 rounded-lg border border-line bg-sheet p-3">
        <div className="flex flex-wrap items-center gap-2">
          <SourceSwitch value={source} onChange={setSource} />
          <Input className="w-64" placeholder={t('tasks.search')} value={q} onChange={(e) => setQ(e.target.value)} />
          {hasLocations && source !== 'manual' && (
            <LocationSelects
              nodes={locations.data!.nodes}
              top={top}
              sub={sub}
              onChange={(a, b) => {
                setTop(a);
                setSub(b);
              }}
            />
          )}
        </div>
        {project.data && source !== 'manual' && (project.data.issueTypes.length > 0 || project.data.attributeDefs.some((a) => a.mirrored)) && (
          <div className="flex flex-wrap items-end gap-2">
            <TaskFieldFilters data={project.data} value={fields} onChange={setFields} />
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <Select className="w-44" value={status} onChange={(e) => setStatus(e.target.value)} aria-label={t('planning.filters.status')}>
            <option value="">{t('planning.filters.allStatus')}</option>
            {statuses.map((x) => (
              <option key={x} value={x}>
                {t(`task.status.${x as 'open'}`, { defaultValue: x })}
              </option>
            ))}
          </Select>
          <Select className="w-44" value={due} onChange={(e) => setDue(e.target.value)} aria-label={t('planning.filters.due')}>
            <option value="">{t('planning.filters.dueAny')}</option>
            <option value="overdue">{t('planning.filters.dueOverdue')}</option>
            <option value="d7">{t('planning.filters.due7')}</option>
            <option value="d14">{t('planning.filters.due14')}</option>
            <option value="none">{t('planning.filters.dueNone')}</option>
          </Select>
          <Select className="w-44" value={planFilter} onChange={(e) => setPlanFilter(e.target.value)} aria-label={t('planning.filters.plan')}>
            <option value="">{t('planning.filters.planAny')}</option>
            <option value="none">{t('planning.filters.planNone')}</option>
            <option value="zero">{t('planning.filters.planZero')}</option>
            <option value="set">{t('planning.filters.planSet')}</option>
          </Select>
          <Select className="w-44" value={bookedFilter} onChange={(e) => setBookedFilter(e.target.value)} aria-label={t('planning.filters.booked')}>
            <option value="">{t('planning.filters.bookedAny')}</option>
            <option value="none">{t('planning.filters.bookedNone')}</option>
            <option value="some">{t('planning.filters.bookedSome')}</option>
          </Select>
          <Select className="w-56" value={sortBy} onChange={(e) => setSortBy(e.target.value)} aria-label={t('planning.filters.sort')}>
            <option value="ref">
              {t('planning.filters.sort')}: {t('planning.filters.sortRef')}
            </option>
            <option value="due">
              {t('planning.filters.sort')}: {t('planning.filters.sortDue')}
            </option>
            <option value="booked">
              {t('planning.filters.sort')}: {t('planning.filters.sortBooked')}
            </option>
            <option value="plan">
              {t('planning.filters.sort')}: {t('planning.filters.sortPlan')}
            </option>
          </Select>
          {quickFilters > 0 && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setStatus('');
                setDue('');
                setPlanFilter('');
                setBookedFilter('');
              }}
            >
              {t('planning.filters.reset')} ({quickFilters})
            </Button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <Checkbox label={t('planning.onlyOpen')} checked={open} onChange={(e) => setOpen(e.target.checked)} />
          <Checkbox label={t('planning.onlyWithoutPlan')} checked={noPlan} onChange={(e) => setNoPlan(e.target.checked)} />
          <Checkbox label={t('projectKpi.onlyOverPlan')} checked={overPlan} onChange={(e) => setOverPlan(e.target.checked)} />
          <span className="ml-auto text-sm text-graphite">{t('tasks.count', { count: rows.length })}</span>
        </div>
      </div>
      {list.isLoading && <Loading />}
      <ErrorBox error={list.error} />
      {list.data?.truncated && <Notice tone="warn">{t('tasks.truncated', { count: rows.length })}</Notice>}
      {list.data && (
        <ScrollTable label={t('planning.tabs.tasks')}>
          <table className="w-full min-w-[980px] text-sm">
            <thead className="border-b border-line bg-sheet-2">
              <tr>
                {canPlan && (
                  <th className={clsx(th, 'w-8')}>
                    <Checkbox label={<span className="sr-only">{t('common.selectAll')}</span>} checked={sel.all} onChange={sel.toggleAll} />
                  </th>
                )}
                <th className={clsx(th, 'min-w-72')}>{t('common.task')}</th>
                <th className={th}>{t('tasks.period')}</th>
                <th className={th}>{t('common.status')}</th>
                <th className={clsx(th, 'text-right')}>{t('planning.planHours')}</th>
                <th className={clsx(th, 'text-right')}>{t('common.booked')}</th>
                <th className={th} />
              </tr>
            </thead>
            <tbody>
              {chunk.visible.map((x) => {
                const link = formaLink(me.settings.deepLinkTemplate, project.data?.project.forma.projectId, x.formaIssueId);
                const use = x.plannedMinutes && x.bookedMinutes !== null ? x.bookedMinutes / x.plannedMinutes : null;
                return (
                  <tr key={x.id} className={clsx('border-b border-line/70', sel.selected.has(x.id) && 'bg-accent-soft/50')}>
                    {canPlan && (
                      <td className={td}>
                        <Checkbox label={<span className="sr-only">{t('common.select')}</span>} {...rowCheck(sel, x.id)} />
                      </td>
                    )}
                    <td className={td}>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="num font-semibold text-accent">{x.ref}</span>
                        <span>{x.title}</span>
                        <TaskBadges formaState={x.formaState} source={x.source} />
                      </div>
                      <LocationLine
                        path={x.locationPath}
                        source={x.source}
                        site={project.data?.project.siteLocation ?? null}
                        attrs={Object.entries(x.attributes)
                          .filter(([id]) => fieldsShown.has(id))
                          .map(([, v]) => String(v))
                          .filter(Boolean)}
                      />
                    </td>
                    <td className={clsx(td, 'num whitespace-nowrap text-graphite', x.statusGroup === 'active' && x.dueDate && x.dueDate < me.today && 'font-semibold text-bad')}>
                      {x.startDate || x.dueDate ? `${x.startDate ? d(x.startDate) : '…'} - ${x.dueDate ? d(x.dueDate) : '…'}` : '-'}
                    </td>
                    <td className={clsx(td, 'whitespace-nowrap')}>{t(`task.status.${x.status as 'open'}`, { defaultValue: x.status })}</td>
                    <td className={clsx(td, 'text-right')}>
                      {canPlan ? (
                        <div className="inline-flex flex-col items-end gap-0.5">
                          <PlanHoursCell minutes={x.plannedMinutes} onSave={(m) => save(x.id, m)} />
                          <span className="text-[11px] text-graphite">
                            {x.plannedMinutes === null ? t('planning.noPlan') : x.plannedMinutes === 0 ? t('projectKpi.notEvaluated') : ''}
                          </span>
                        </div>
                      ) : x.plannedMinutes !== null ? (
                        <span className="num">{h(x.plannedMinutes, 1)}</span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className={clsx(td, 'num text-right whitespace-nowrap')}>
                      <span className="inline-flex items-center gap-2">
                        {!!x.plannedMinutes && <PlanBar planned={x.plannedMinutes} booked={x.bookedMinutes ?? 0} className="w-16" />}
                        <span className={clsx(use !== null && use > 1 && 'font-semibold text-warn')}>{h(x.bookedMinutes)}</span>
                        {use !== null && <span className="w-10 text-xs text-graphite">{pct(use)}</span>}
                      </span>
                    </td>
                    <td className={clsx(td, 'text-right')}>
                      {link && (
                        <FormaIssueLink href={link} className="inline-flex rounded p-1.5 text-graphite hover:text-accent" title={t('task.openInForma')}>
                          <ExternalLink className="size-3.5" />
                        </FormaIssueLink>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {chunk.hasMore && <Sentinel onMore={chunk.more} />}
        </ScrollTable>
      )}
      {chunk.hasMore && <p className="text-xs text-graphite">{t('entries.showing', { shown: chunk.shown, total: chunk.total })}</p>}
      <p className="text-xs text-graphite">{t('planning.legend')}</p>
      {sel.count > 0 && canPlan && (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-sheet px-4 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.06)]">
          <div className="mx-auto flex max-w-[1400px] flex-wrap items-center gap-2">
            <span className="text-sm">{t('common.selected', { count: sel.count })}</span>
            <span className="text-xs text-graphite">{t('common.shiftHint')}</span>
            <div className="ml-auto flex gap-2">
              <Button onClick={() => applyBulk(null)} loading={setPlanMany.isPending}>
                {t('planning.bulkRemove')}
              </Button>
              <Button variant="primary" onClick={() => setBulk(true)}>
                {t('planning.bulkSet')}
              </Button>
            </div>
          </div>
        </div>
      )}
      <Modal
        open={bulk}
        onClose={() => setBulk(false)}
        title={t('planning.bulkSet')}
        footer={
          <>
            <Button onClick={() => setBulk(false)}>{t('common.cancel')}</Button>
            <Button variant="primary" disabled={bulkMinutes === null} loading={setPlanMany.isPending} onClick={() => applyBulk(bulkMinutes)}>
              {t('common.apply')}
            </Button>
          </>
        }
      >
        <Field label={t('planning.planHours')} hint={t('planning.bulkHint', { count: sel.count })}>
          <DecimalHours minutes={bulkMinutes} onChange={setBulkMinutes} />
        </Field>
      </Modal>
    </div>
  );
}

/** Weekly staffing of one project: planned hours per person and week (allocations). */
function StaffPlanTab({ projectId }: { projectId: string }) {
  const { t } = useTranslation();
  const toast = useToast();
  const range = useWeekRange(10);
  const q = useQ('workload.project', { projectId, from: range.from, to: range.to }, { enabled: !!projectId });
  const set = useM('workload.setAllocation', { invalidate: ['workload.'] });
  const fill = useM('workload.fillAllocation', { invalidate: ['workload.'] });
  const [series, setSeries] = useState<{ personId: string; name: string } | null>(null);
  const [seriesFrom, setSeriesFrom] = useState(range.from);
  const [seriesTo, setSeriesTo] = useState(range.to);
  const [seriesMinutes, setSeriesMinutes] = useState<number | null>(null);
  const data = q.data;
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-line bg-sheet px-3 py-2">
        <RangeNav range={range} />
      </div>
      <Notice tone="neutral">{data && !data.showCapacity ? `${t('workload.planHint')} ${t('workload.leadPrivacy')}` : t('workload.planHint')}</Notice>
      {q.isLoading && <Loading />}
      <ErrorBox error={q.error} />
      {data && (
        <ScrollTable label={t('planning.tabs.staff')}>
          <table className="w-full min-w-[900px] text-[13px]">
            <thead className="border-b border-line bg-sheet-2">
              <tr>
                <th className={clsx(th, 'sticky left-0 z-10 bg-sheet-2')}>{t('common.person')}</th>
                {data.weeks.map((w) => (
                  <th key={w} className={clsx(th, 'text-center')}>
                    {t('common.cw')} {kw(w)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.persons.map((p) => (
                <tr key={p.id} className="border-b border-line/70">
                  <td className={clsx(td, 'sticky left-0 z-10 bg-sheet whitespace-nowrap')}>
                    <div className="font-medium">{p.name}</div>
                    <div className="flex items-center gap-1 text-xs text-graphite">
                      {p.role === 'lead' && <Pill tone="accent">{t('projects.role.lead')}</Pill>}
                      {[p.discipline, p.locationCountry].filter(Boolean).join(' · ')}
                    </div>
                    {data.canEdit && (
                      <button className="mt-1 text-xs text-accent hover:underline" onClick={() => setSeries({ personId: p.id, name: p.name })}>
                        {t('workload.fill')}
                      </button>
                    )}
                  </td>
                  {p.weeks.map((w) => (
                    <td key={w.weekStart} className="px-1 py-1 text-center align-top">
                      {data.canEdit ? (
                        <CellInput
                          value={w.plannedMinutes}
                          onSave={(m) =>
                            set.mutateAsync({ projectId, personId: p.id, weekStart: w.weekStart, minutes: m ?? 0 }).catch(toast.error)
                          }
                        />
                      ) : (
                        <span className="num font-semibold">{h(w.plannedMinutes, 0)}</span>
                      )}
                      {w.level && (
                        <div
                          className={clsx('num mt-0.5 flex items-center justify-center gap-0.5 rounded px-0.5 text-[11px] text-ink', LEVEL_BG[w.level])}
                          title={
                            data.showCapacity
                              ? t('workload.cellTitle', { cap: h(w.capacityMinutes ?? 0), plan: h(w.plannedAllProjectsMinutes ?? 0), booked: h(w.bookedMinutes) })
                              : t('workload.levelTitle', { level: t(`workload.level.${w.level}`) })
                          }
                        >
                          {w.level === 'over' && <AlertTriangle className="size-2.5 text-bad" aria-hidden />}
                          {data.showCapacity ? `${h(w.plannedAllProjectsMinutes ?? 0, 0)}/${h(w.capacityMinutes ?? 0, 0)}` : t(`workload.levelShort.${w.level}`)}
                        </div>
                      )}
                      {w.bookedMinutes > 0 && <div className="num text-[11px] text-graphite">{h(w.bookedMinutes, 1)}</div>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollTable>
      )}
      <Modal
        open={!!series}
        onClose={() => setSeries(null)}
        title={`${t('workload.fillTitle')}: ${series?.name ?? ''}`}
        footer={
          <>
            <Button onClick={() => setSeries(null)}>{t('common.cancel')}</Button>
            <Button
              variant="primary"
              disabled={seriesMinutes === null}
              loading={fill.isPending}
              onClick={() =>
                fill
                  .mutateAsync({ projectId, personId: series!.personId, from: seriesFrom, to: seriesTo, minutesPerWeek: seriesMinutes ?? 0 })
                  .then(() => setSeries(null))
                  .catch(toast.error)
              }
            >
              {t('common.apply')}
            </Button>
          </>
        }
      >
        <div className="flex flex-wrap gap-4">
          <Field label={t('common.from')}>
            <Input type="date" value={seriesFrom} onChange={(e) => setSeriesFrom(e.target.value)} />
          </Field>
          <Field label={t('common.to')}>
            <Input type="date" value={seriesTo} onChange={(e) => setSeriesTo(e.target.value)} />
          </Field>
          <Field label={t('workload.perWeek')}>
            <HoursInput value={seriesMinutes} onChange={setSeriesMinutes} />
          </Field>
        </div>
      </Modal>
    </div>
  );
}

/**
 * Where a task sits: project site, location in the Forma project, sub-location and the fields taken over from Forma
 * (trade, floor ...). Manual tasks have no location in Forma, so nothing is claimed for them.
 */
function LocationLine({ path, source, site, attrs }: { path: string | null; source: string; site: string | null; attrs: string[] }) {
  const { t } = useTranslation();
  const parts = (path ?? '').split(' / ').filter(Boolean);
  const location = parts[0];
  const sub = parts.slice(1).join(' › ');
  return (
    <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-graphite">
      {site && (
        <span>
          <span className="font-medium">{t('planning.site')}:</span> {site}
        </span>
      )}
      {(location || source === 'forma') && (
        <span>
          <span className="font-medium">{t('planning.location')}:</span> {location ?? t('planning.noLocation')}
        </span>
      )}
      {sub && (
        <span>
          <span className="font-medium">{t('planning.subLocation')}:</span> {sub}
        </span>
      )}
      {attrs.length > 0 && <span>{attrs.join(' · ')}</span>}
    </div>
  );
}

const MAX_PLAN_HOURS = 1500;

function toText(minutes: number | null) {
  if (minutes === null) return '';
  return String(Math.round((minutes / 60) * 100) / 100).replace('.', ',');
}

/** "12", "12,5" or "12.5" hours; empty = null; invalid = undefined. */
function parseDecimalHours(text: string): number | null | undefined {
  const s = text.trim().replace(',', '.');
  if (!s) return null;
  if (!/^\d{1,4}(\.\d{1,2})?$/.test(s)) return undefined;
  const n = Number(s);
  return n <= MAX_PLAN_HOURS ? Math.round(n * 60) : undefined;
}

/** Hours field for plans (task plans can exceed 99 h, so no h:mm format here). */
function DecimalHours({ minutes, onChange }: { minutes: number | null; onChange: (m: number | null) => void }) {
  const [text, setText] = useState(toText(minutes));
  return (
    <Input
      inputMode="decimal"
      className="num w-28"
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        const m = parseDecimalHours(e.target.value);
        if (m !== undefined) onChange(m);
      }}
    />
  );
}

/**
 * Inline plan of one task. Empty = no plan yet; 0 = the task is not evaluated (no plan/actual ratio).
 */
function PlanHoursCell({ minutes, onSave }: { minutes: number | null; onSave: (m: number | null) => void }) {
  const [text, setText] = useState(toText(minutes));
  const [bad, setBad] = useState(false);
  useEffect(() => {
    setText(toText(minutes));
    setBad(false);
  }, [minutes]);
  const commit = () => {
    const m = parseDecimalHours(text);
    if (m === undefined) {
      setBad(true);
      return;
    }
    setBad(false);
    if (m !== minutes) onSave(m);
  };
  return (
    <Input
      inputMode="decimal"
      aria-invalid={bad || undefined}
      className={clsx('num h-7! w-20 px-1.5 text-right', bad && 'border-bad')}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        if (e.key === 'Escape') {
          setText(toText(minutes));
          setBad(false);
        }
      }}
    />
  );
}
